import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { SubscriptionStatus } from "@/api/types";
import { useSubscriptionStatus } from "@/api/queries";
import { onSubscriptionSuspended } from "@/api/http/suspended";
import { BooksLockedPage } from "@/features/system/BooksLockedPage";
import {
  PAYMENT_RETURN_PATH,
  PaymentReturnPage,
} from "@/features/settings/subscription/PaymentReturnPage";
import { allows } from "./plan";

interface SubscriptionContextValue {
  /** The last status the API gave, or undefined while it is still being asked. */
  status: SubscriptionStatus | undefined;
  /** Asks the API again, now. */
  recheck: () => void;
  /** Whether a re-check is in flight, so a button can say so. */
  rechecking: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

/**
 * What the shell learns about billing on boot — the `session.tsx` pattern, for the other thing the
 * whole application depends on.
 *
 * Two sources, and they are not redundant. `GET /api/v1/subscription-status` is the answer; a 402
 * from any other call is the *symptom*, and it can arrive first — the status is a minute stale by
 * design, and the tab may have been open since before ops suspended the organisation. So a refusal
 * from the transport flips the screen immediately, and the next status that says otherwise takes
 * it back down. Neither alone is enough: the status alone would leave a suspended customer
 * watching every panel fail for up to a minute, and the 402 alone would have nothing to clear it.
 *
 * The suspended screen replaces the shell rather than sitting inside it. Everything the shell
 * offers is refused while the block is on; a navigation bar over a wall of failures is worse than
 * one page that says what happened.
 */
export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { data, dataUpdatedAt, refetch, isFetching } = useSubscriptionStatus();
  // When the transport last saw a 402, or null for never. A moment rather than a flag, because
  // clearing it correctly is a question about *order*: see below.
  const [refusedAt, setRefusedAt] = useState<number | null>(null);

  useEffect(() => onSubscriptionSuspended(() => setRefusedAt(Date.now())), []);

  // A refusal stands until a status fetched *after* it disagrees. Not merely until any status
  // disagrees: the status in hand may have been fetched before the suspension — that is the
  // ordinary case, since it is a minute stale by design and the request that drew the 402 was made
  // later. Believing it would put the customer back into an application that refuses every click,
  // which is precisely the state this screen exists to replace.
  //
  // Derived rather than cleared in an effect, so there is one render and one truth: the refusal
  // and the status are two observations with timestamps, and which one holds is a comparison, not
  // a sequence of state updates.
  const superseded =
    refusedAt !== null && data !== undefined && !data.blocked && dataUpdatedAt > refusedAt;

  const recheck = useCallback(() => void refetch(), [refetch]);

  if (data?.blocked === true || (refusedAt !== null && !superseded)) {
    // A suspended company paying its bill comes back from the gateway here, and must see whether it was paid
    // (suite ADR-0102). Leaving it reloads the app, which asks the status again.
    if (window.location.pathname === PAYMENT_RETURN_PATH) {
      return <PaymentReturnPage onDone={() => window.location.assign("/")} />;
    }
    return <BooksLockedPage status={data} onRecheck={recheck} rechecking={isFetching} />;
  }

  return (
    <SubscriptionContext.Provider value={{ status: data, recheck, rechecking: isFetching }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * The subscription state, for the shell.
 *
 * Answers `undefined` outside a provider rather than throwing, unlike `useCurrentSession`: the
 * banner is an addition to a screen, and a component tree that renders one without a provider —
 * a test of the shell, say — should render the screen, not fail.
 */
export function useSubscription(): SubscriptionContextValue | null {
  return useContext(SubscriptionContext);
}

/** Whether the plan includes a feature. No subscription, or no list, holds nothing back. */
export function useFeature(code: string): boolean {
  return allows(useContext(SubscriptionContext)?.status, code);
}
